import Endpoints from 'constants/Endpoints';
import {RestRequestDELETE, RestRequestGET, RestRequestPOST} from 'libs/RestRequest';
import {
    clearResource,
    setResource,
    setSaveStatusClosed,
    setSaveStatusDeleted,
    setSaveStatusDialog,
    setSaveStatusError,
    setSaveStatusLoading,
    setStepperState
} from 'redux/actions'
import reduxStore from 'redux/store'
import RouterPath, {createPath} from 'libs/RouterPath';
import Tr from 'translations/Translations';
import {DemandValidation} from 'constants/DemandValidation';
import objectExtension from 'libs/objectExtension'
import querystring from 'querystring';
import {ProcedureType} from "../constants/ProcedureType";
import DemandSectionsService from "./DemandSectionsService";
import PrivilegeService from "./PrivilegeService";

const DEMAND_RESOURCE_ID = 'demand';
const DEMAND_AVAILABLE_FORM_TYPES_RESOURCE_ID = 'demandAvailableFormTypes';
const DEMAND_AVAILABLE_REALIZATIONS_RESOURCE_ID = 'demandRealizationTypes';
const DEMAND_AVAILABLE_ORGANIZATIONS_RESOURCE_ID = 'demandAvailableOrganizations';
const DEMAND_NUMBER_AUTOMATED = 'demandNumberAutomated';
const DEMAND_CASE_CLASSIFICATION_SYMBOL_REQUIRED = 'caseClassificationSymbolRequired';


const createParams = (params, defaultProps = {
    procedureType: ProcedureType.EUROPE_PUBLIC,
    passive: false,
    exPost: false
}) => {
    const {procedureType, passive, exPost} = params;
    let queryParams = {}
    if (passive) {
        queryParams['passive'] = passive;
    } else if (defaultProps.passive) {
        queryParams['passive'] = defaultProps.passive;
    }

    if (procedureType) {
        queryParams['procedureType'] = procedureType;
    } else if (defaultProps.procedureType) {
        queryParams['procedureType'] = defaultProps.procedureType;
    }

    if (exPost) {
        queryParams['exPost'] = exPost;
    } else if (defaultProps.exPost) {
        queryParams['exPost'] = defaultProps.exPost;
    }

    return `?${querystring.stringify(queryParams)}`
}

const get = async (id, params) => {
    const res = await RestRequestGET(Endpoints.DEMAND_GET_BY_ID(id, createParams(params, {})));
    reduxStore.dispatch(setResource(DEMAND_RESOURCE_ID, res.demand));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_REALIZATIONS_RESOURCE_ID, res.demandAvailableRealizationTypes));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_ORGANIZATIONS_RESOURCE_ID, res.demandAvailableOrganizations));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_FORM_TYPES_RESOURCE_ID, res.demandAvailableFormTypes));
    reduxStore.dispatch(setResource(DEMAND_NUMBER_AUTOMATED, res.numberingAutomated));
    reduxStore.dispatch(setResource(DEMAND_CASE_CLASSIFICATION_SYMBOL_REQUIRED, res.caseClassificationSymbolRequired));
    return res;
}

const getCreateNew = async (params) => {
    const res = await RestRequestGET(Endpoints.DEMAND_GET_BY_ID(-1, createParams(params)));
    reduxStore.dispatch(setResource(DEMAND_RESOURCE_ID, res.demand));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_REALIZATIONS_RESOURCE_ID, res.demandAvailableRealizationTypes));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_ORGANIZATIONS_RESOURCE_ID, res.demandAvailableOrganizations));
    reduxStore.dispatch(setResource(DEMAND_AVAILABLE_FORM_TYPES_RESOURCE_ID, res.demandAvailableFormTypes));
    reduxStore.dispatch(setResource(DEMAND_NUMBER_AUTOMATED, res.numberingAutomated));
    reduxStore.dispatch(setResource(DEMAND_CASE_CLASSIFICATION_SYMBOL_REQUIRED, res.caseClassificationSymbolRequired));
}

const _formatFormValues = (values) => {
    const {
        identity,
        enableOptional,
        termOption,
        termDate,
        termDescription,
        orderValueDecisionDate,
        auctionMade,
        digitalSign,
        euFunded,
        ...other
    } = values;

    const formatDate = (x) => (x ? x.format() : x);

    return {
        identity: identity,
        agreementRealizationDate: {
            date: termOption === 'date' ? formatDate(termDate) : null,
            text: termOption === 'text' ? termDescription : null,
        },
        orderValueDecisionDate: formatDate(orderValueDecisionDate),
        auctionMade: auctionMade === 'true',
        digitalSign: digitalSign === 'true',
        euFunded: euFunded === 'true',
        ...other
    }
}

const _save = async (url, values) => {
    const data = _formatFormValues(values)
    const {resources} = reduxStore.getState()
    const {demand} = resources;
    const diff = objectExtension.diff(data, demand);
    if (diff === null) return null;

    try {
        return await RestRequestPOST(url, data)
    } catch (error) {
        throw error;
    }
}

const getValidate = async (id = -1, clear = true) => {
    if (id === -1) {
        if (clear) {
            reduxStore.dispatch(clearResource(DemandSectionsService.DEMAND_VALIDATION_RESOURCE_ID))
        }
        const data = [
            {
                section: DemandValidation.DEMAND,
                status: "EMPTY",
                required: true,
            }
        ]
        reduxStore.dispatch(setResource(DemandSectionsService.DEMAND_VALIDATION_RESOURCE_ID, data))
        return;
    }

    await DemandSectionsService.getPreparingSections(id, clear)
}

const save = async (values) => {
    reduxStore.dispatch(setSaveStatusLoading());

    const res = await _save(Endpoints.DEMAND_SAVE, values)
    await _handleSaveResponse(res);
}

const saveExPost = async (values) => {
    reduxStore.dispatch(setSaveStatusLoading());

    const res = await _save(Endpoints.DEMAND_SAVE_EX_POST, values)
    await _handleSaveResponse(res);
}

const _handleSaveResponse = async (res) => {
    try {
        if (res === null) {
            reduxStore.dispatch(setSaveStatusDialog());
            reduxStore.dispatch(setStepperState())
        } else if (res.success) {
            reduxStore.dispatch(setSaveStatusDialog());
            reduxStore.dispatch(setStepperState())

            const path = createPath(RouterPath.myProcedure.steps.MP_1_ID, {id: res.result})
            if (path === window.location.pathname) {
                await getValidate(res.result, false)
            } else {
                RouterPath.redirect(path)
            }
        } else {
            let msg;

            for (let prop in res.errors) {
                if (Object.prototype.hasOwnProperty.call(res.errors, prop)) {
                    if (prop === 'number') {
                        msg = res.errors[prop];
                    } else if (prop.startsWith('fundingInstitutionLogo')) {
                        msg = Tr('funding_institution_logo_error');
                    } else if (prop === 'ezdrpId' || prop === 'ezdpuwId') {
                        msg =  Array.isArray(res.errors[prop]) ? res.errors[prop][0] : res.errors[prop];
                    } else if (prop === 'name') {
                        if(Array.isArray(res.errors[prop]))
                            msg = res.errors[prop][0];
                        else if(typeof res.errors[prop] === 'string')
                            msg = res.errors[prop];
                    }
                }
                if (msg) {
                    break;
                }
            }

            if (!msg) {
                msg = Tr('unknown_error_message');
            }
            reduxStore.dispatch(setSaveStatusError(msg));
        }
    } catch (error) {
    }
}

const preview = async (id = -1) => {

    try {
        const res = await RestRequestGET(Endpoints.DEMAND_PREVIEW(id))
        window.location.href = res.url
    } catch (error) {
    }

}

// const autoSave = async (id = -1) => {
// 	const { formValues } = reduxStore.getState()
// 	const values = formValues['mp_1_demand']
// 	if (values === undefined) return;
//
// 	try {
// 		await _save(Endpoints.DEMAND_AUTOSAVE(id), values)
// 	} catch (error) {
// 	}
// }
//
// const autoSaveCheck = async (id = -1) => {
// 	try {
// 		return await RestRequestGET(Endpoints.DEMAND_AUTOSAVE_CHECK(id))
// 	} catch (error) {
// 	}
// }
//
// const autoSaveDelete = async (id = -1) => {
// 	try {
// 		return await RestRequestDELETE(Endpoints.DEMAND_AUTOSAVE(id))
// 	} catch (error) {
// 	}
// }
//
// const autoSaveLoad = async (id = -1) => {
// 	try {
// 		const res = await RestRequestGET(Endpoints.DEMAND_AUTOSAVE(id))
// 		reduxStore.dispatch(
// 			setResource(DEMAND_RESOURCE_ID, res)
// 		)
// 	} catch (error) {
// 	}
// }
//
// const restoreAutosave = async (id = -1) => {
// 	await autoSaveLoad(id);
// }
//
// const getDemandActions = async (id = -1) => {
// 	try {
// 		let res = await RestRequestGET(Endpoints.DEMAND_ACTIONS(id))
// 		reduxStore.dispatch(setResource("demandActions", res))
// 	} catch (error) { }
// }

const deteleUrl = async (url) => {
    try {
        await RestRequestDELETE(url);
    } catch (error) {
    }
}

const action = async (url) => {
    try {
        await RestRequestGET(url);
    } catch (error) {
    }
}

const deleteDemand = async (id) => {
    reduxStore.dispatch(setSaveStatusLoading());
    try {
        await RestRequestDELETE(Endpoints.DEMAND_DELETE(id));
        reduxStore.dispatch(setSaveStatusDeleted());
    } catch (error) {
        reduxStore.dispatch(setSaveStatusError());
    }
}

const clone = async (id) => {
    reduxStore.dispatch(setSaveStatusLoading());
    try {
        const res = await RestRequestPOST(Endpoints.DEMAND_CLONE(id))
        if (res.success) {
            reduxStore.dispatch(setSaveStatusClosed());
            const path = createPath(RouterPath.myProcedure.steps.MP_1_ID, {id: res.result})
            RouterPath.redirect(path)
        } else {
        }
    } catch (error) {
    }
}

const clearResources = (resource) => reduxStore.dispatch(clearResource(resource));

const getRelatedObjects = async (id) => {
    return await RestRequestGET(Endpoints.DEMAND_REALTED_OBJECTS(id));
}
const getBuyerPrivileges = async (id) => {
    await PrivilegeService.getPrivileges(Endpoints.DEMAND_PRIVILEGES(id), PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY);
}
const getBuyerRfxPrivileges = async (id) => {
    await PrivilegeService.getPrivileges(Endpoints.DEMAND_RFX_PRIVILEGES(id), PrivilegeService.RFX_PRIVILEGE_STORE_KEY);
}

const updateNumber = async (id) => {
    return await RestRequestPOST(Endpoints.DEMAND_UPDATE_NUMBER(id));
}

const checkForDraft = async (id) => {
    return await RestRequestGET(Endpoints.DEMAND_AUTOSAVE_CHECK(id));
}

const loadDraft = async (id) => {
    const res = await RestRequestGET(Endpoints.DEMAND_AUTOSAVE(id));
    reduxStore.dispatch(setResource(DEMAND_RESOURCE_ID, res.result));
    return res;
}

const saveDraft = async (id, formValues) => {
    await RestRequestPOST(Endpoints.DEMAND_AUTOSAVE(id), formValues)
}

const deleteDraft = async (id) => {
    await RestRequestDELETE(Endpoints.DEMAND_AUTOSAVE(id));
}

const checkForDraftExPost = async (id) => {
    return await RestRequestGET(Endpoints.DEMAND_EXPOST_AUTOSAVE_CHECK(id));
}

const loadDraftExPost = async (id) => {
    const res = await RestRequestGET(Endpoints.DEMAND_EXPOST_AUTOSAVE(id));
    reduxStore.dispatch(setResource(DEMAND_RESOURCE_ID, res.result));
    return res;
}

const saveDraftExPost = async (id, formValues) => {
    await RestRequestPOST(Endpoints.DEMAND_EXPOST_AUTOSAVE(id), formValues)
}

const deleteDraftExPost = async (id) => {
    await RestRequestDELETE(Endpoints.DEMAND_EXPOST_AUTOSAVE(id));
}

export default {
    get,
    getCreateNew,
    save,
    saveExPost,
    clone,
    getValidate,
    preview,
    //getDemandActions,
    deteleUrl,
    action,
    deleteDemand,
    clearResources,
    getRelatedObjects,
    getBuyerPrivileges,
    getBuyerRfxPrivileges,
    updateNumber,
    checkForDraft,
    loadDraft,
    saveDraft,
    deleteDraft,
    checkForDraftExPost,
    loadDraftExPost,
    saveDraftExPost,
    deleteDraftExPost,
    DEMAND_RESOURCE_ID,
    DEMAND_AVAILABLE_REALIZATIONS_RESOURCE_ID,
    DEMAND_AVAILABLE_ORGANIZATIONS_RESOURCE_ID,
    DEMAND_AVAILABLE_FORM_TYPES_RESOURCE_ID,
    DEMAND_NUMBER_AUTOMATED,
    DEMAND_CASE_CLASSIFICATION_SYMBOL_REQUIRED
}